import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { LAB_SETTINGS_DEFAULTS, type DeliveryChannel, OrderDetail, Patient, PatientSummary, Quote } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { fmtDate, fmtDateTime, fullName, money, todayIso } from "../lib/format.js";
import { useBranch, useSession } from "../lib/session.js";
import { QUOTE_STATE_LABEL, quoteTone } from "../lib/status.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { PatientForm, PatientSearch, SelectedPatient } from "../components/PatientPicker.js";
import { DOCTOR_NONE, DoctorPicker, type DoctorChoice } from "../components/DoctorPicker.js";
import { computeDiscount, DiscountEditor, LinesList, NO_DISCOUNT, StudySearch, Totals, type DiscountState, type Line } from "../components/StudyPicker.js";
import { QuotePrintButton } from "../components/PrintActions.js";
import { QuoteWhatsAppSheet } from "../components/QuoteWhatsApp.js";
import { Alert, Button, Card, Chip, Definition, Empty, Field, Input, Loading, Money, PageHead, Pill, Segmented, Sheet, Textarea } from "../components/ui.js";

export function QuotesPage() {
  const branch = useBranch();
  const validityDays = useSession().me?.lab_settings?.quote_validity_days ?? LAB_SETTINGS_DEFAULTS.quote_validity_days;
  const navigate = useNavigate();
  const [state, setState] = useState<"open" | "converted" | "all">("open");
  const quotes = useQuery(() => get("quotes", { query: { branch_id: branch.id, ...(state === "all" ? {} : { state }), limit: 100 } }), [branch.id, state]);
  return (
    <>
      <PageHead
        kicker={branch.name}
        title="Cotizaciones"
        lede={`Válidas ${validityDays} días. Una cotización se convierte en solicitud con un toque, respetando el precio cotizado.`}
        actions={
          <Button variant="primary" size="lg" icon="plus" onClick={() => navigate("/cotizaciones/nueva")}>
            Nueva cotización
          </Button>
        }
      />
      <div className="toolbar">
        <Segmented value={state} onChange={setState} options={[{ value: "open", label: "Vigentes" }, { value: "converted", label: "Convertidas" }, { value: "all", label: "Todas" }]} />
      </div>
      <Card pad={false}>
        {quotes.loading && !quotes.data ? (
          <Loading />
        ) : !quotes.data?.items.length ? (
          <Empty icon="quote" title="Sin cotizaciones" hint="Crea una para un paciente o para alguien que pregunta por precios." />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Para</th>
                  <th>Estudios</th>
                  <th>Vence</th>
                  <th>Estado</th>
                  <th className="right">Total</th>
                </tr>
              </thead>
              <tbody>
                {quotes.data.items.map((q) => (
                  <tr key={q.id} className="clickable" onClick={() => navigate(`/cotizaciones/${q.id}`)}>
                    <td className="folio">{q.folio}</td>
                    <td style={{ fontWeight: 600 }}>
                      <QuoteName q={q} />
                    </td>
                    <td className="muted small">{q.lines.map((l) => l.clave).join(", ")}</td>
                    <td className="muted">{q.valid_until ? fmtDate(q.valid_until) : "—"}</td>
                    <td>
                      <Chip tone={quoteTone(q.state)}>{QUOTE_STATE_LABEL[q.state]}</Chip>
                    </td>
                    <td className="right">
                      <Money cents={q.total_cents} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function QuoteName({ q }: { q: Quote }) {
  const patient = useQuery(() => get("patient", { params: { id: q.patient_id! } }), [q.patient_id], { enabled: !!q.patient_id });
  if (q.patient_id) return <>{patient.data ? fullName(patient.data) : "…"}</>;
  return <>{q.prospect_name ?? "Sin nombre"}</>;
}

export function QuoteNewPage() {
  const branch = useBranch();
  const validityDays = useSession().me?.lab_settings?.quote_validity_days ?? LAB_SETTINGS_DEFAULTS.quote_validity_days;
  const toast = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"patient" | "prospect">("prospect");
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [prospect, setProspect] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [discount, setDiscount] = useState<DiscountState>(NO_DISCOUNT);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const subtotal = lines.reduce((s, l) => s + (l.amount_cents ?? 0), 0);
  const disc = computeDiscount(subtotal, discount);
  const total = Math.max(0, subtotal - disc.amount);
  const canSave = lines.length > 0 && (mode === "patient" ? !!patient : prospect.trim().length >= 2);

  async function save() {
    setBusy(true);
    try {
      const q = await run<"quote.create", Quote>(
        "quote.create",
        {
          branch_id: branch.id,
          patient_id: mode === "patient" ? (patient?.id ?? null) : null,
          prospect_name: mode === "prospect" ? prospect.trim() : null,
          price_list_id: null,
          lines: lines.map((l) => ({ kind: l.kind, id: l.id, expected_amount_cents: l.amount_cents })),
          discount: disc.input,
          valid_until: null, // the server applies the laboratory's quote_validity_days
          notes: notes.trim() || null,
        },
        { branch_id: branch.id },
      );
      toast.success(`Cotización ${q.folio} guardada.`);
      navigate(`/cotizaciones/${q.id}`, { replace: true });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead kicker={branch.name} title="Nueva cotización" lede={`Se imprime con la vigencia de ${validityDays} días y las indicaciones de ayuno de cada estudio.`} actions={<Button variant="ghost" icon="chevronLeft" onClick={() => navigate(-1)}>Volver</Button>} />
      <div className="split">
        <div className="stack-lg">
          <Card>
            <div className="section-title">
              <span className="step">1</span>
              <h2 className="h2">¿Para quién?</h2>
            </div>
            <div className="stack-lg">
              <Segmented value={mode} onChange={setMode} options={[{ value: "prospect", label: "Solo un nombre" }, { value: "patient", label: "Paciente registrado" }]} />
              {mode === "prospect" ? (
                <Field label="Nombre">
                  <Input value={prospect} onChange={(e) => setProspect(e.target.value)} placeholder="Como quiere que aparezca en la cotización" autoFocus className="lg" />
                </Field>
              ) : patient ? (
                <SelectedPatient patient={patient} onChange={() => setPatient(null)} />
              ) : (
                <div className="stack">
                  <PatientSearch onPick={setPatient} autoFocus />
                  <div className="row" style={{ justifyContent: "center" }}>
                    <Button variant="secondary" icon="userPlus" onClick={() => setShowNew(true)}>
                      Registrar paciente nuevo
                    </Button>
                  </div>
                </div>
              )}
              <Field label="Notas en la cotización" optional>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Promoción, horario de toma, indicaciones adicionales…" />
              </Field>
            </div>
          </Card>
        </div>
        <div className="stack-lg">
          <Card>
            <div className="section-title">
              <span className="step">2</span>
              <h2 className="h2">Estudios</h2>
            </div>
            <div className="stack-lg">
              <StudySearch selected={lines} onPick={(it) => setLines((ls) => [...ls, it])} branchId={branch.id} />
              <LinesList lines={lines} onRemove={(l) => setLines((ls) => ls.filter((x) => !(x.kind === l.kind && x.id === l.id)))} />
              {lines.length > 0 && <DiscountEditor subtotal={subtotal} value={discount} onChange={setDiscount} />}
              <Totals subtotal={subtotal} discount={disc.amount} total={total} />
            </div>
          </Card>
          <Button variant="primary" size="lg" block icon="check" disabled={!canSave} busy={busy} onClick={() => void save()}>
            Guardar cotización
          </Button>
        </div>
      </div>
      <Sheet open={showNew} onClose={() => setShowNew(false)} title="Registrar paciente" wide>
        <PatientForm
          onSaved={(p: Patient) => {
            setPatient(p);
            setShowNew(false);
          }}
          onCancel={() => setShowNew(false)}
        />
      </Sheet>
    </>
  );
}

export function QuotePage() {
  const { id = "" } = useParams();
  const branch = useBranch();
  const toast = useToast();
  const navigate = useNavigate();
  const quote = useQuery(() => get("quote", { params: { id } }), [id]);
  const ticket = useQuery(() => get("quoteTicket", { params: { id } }), [id]);
  const [converting, setConverting] = useState(false);
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [doctor, setDoctor] = useState<DoctorChoice>(DOCTOR_NONE);
  const [channels, setChannels] = useState<DeliveryChannel[]>(["whatsapp", "print"]);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  if (quote.loading && !quote.data) return <Loading />;
  if (!quote.data) return <Empty icon="warning" title="No se encontró la cotización" hint={quote.error ?? undefined} />;
  const q = quote.data;
  const expired = q.valid_until ? q.valid_until < todayIso() : false;

  async function convert() {
    setBusy(true);
    try {
      const order = await run<"quote.convert", OrderDetail>(
        "quote.convert",
        {
          quote_id: q.id,
          patient_id: q.patient_id ?? patient?.id ?? null,
          doctor_id: doctor.doctor_id,
          doctor_text: doctor.doctor_text,
          delivery_channels: channels,
          clinical_notes: null,
          fasting_hours: null,
          condition: null,
          pending_tubes: [],
        },
        { branch_id: branch.id },
      );
      toast.success(`Solicitud ${order.folio} creada desde la cotización.`);
      navigate(`/solicitudes/${order.id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead
        kicker={`Cotización · ${fmtDateTime(q.created_at)}`}
        title={
          <span className="row wrap" style={{ gap: 14 }}>
            <span className="folio">{q.folio}</span>
            <Chip tone={quoteTone(q.state)} large>
              {QUOTE_STATE_LABEL[q.state]}
            </Chip>
          </span>
        }
        lede={ticket.data ? `Para ${ticket.data.customer_name}` : undefined}
        actions={
          <>
            <Button variant="ghost" icon="chevronLeft" onClick={() => navigate("/cotizaciones")}>
              Volver
            </Button>
            <QuotePrintButton quoteId={q.id} />
            <Button variant="secondary" icon="whatsapp" onClick={() => setSending(true)}>
              Enviar por WhatsApp
            </Button>
            {q.state === "open" && (
              <Button variant="primary" icon="arrowRight" onClick={() => setConverting(true)}>
                Convertir en solicitud
              </Button>
            )}
            {q.state === "converted" && q.converted_order_id && (
              <Link to={`/solicitudes/${q.converted_order_id}`} className="btn btn-secondary">
                Ver solicitud
              </Link>
            )}
          </>
        }
      />
      <div className="split">
        <Card title="Estudios cotizados" pad={false}>
          <div className="table-scroll">
            <table className="table">
              <tbody>
                {q.lines.map((l) => (
                  <tr key={`${l.kind}:${l.id}`}>
                    <td className="mono muted">{l.clave}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{l.name}</div>
                      {l.study_claves.length > 1 && <div className="small faint">{l.study_claves.join(", ")}</div>}
                      {l.indications && <div className="small muted">{l.indications}</div>}
                    </td>
                    <td className="right">
                      <Money cents={l.amount_cents} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <div className="stack-lg">
          <Card title="Resumen">
            <Totals subtotal={q.subtotal_cents} discount={q.discount_cents} total={q.total_cents} />
            <div className="grid-2" style={{ marginTop: 18 }}>
              <Definition k="Vigente hasta" v={q.valid_until ? fmtDate(q.valid_until) : "—"} />
              <Definition k="Emitida por" v={ticket.data?.issued_by ?? "…"} />
            </div>
            {q.notes && <p className="muted" style={{ marginTop: 12 }}>{q.notes}</p>}
            {expired && q.state === "open" && (
              <div style={{ marginTop: 14 }}>
                <Alert tone="amber">La vigencia venció; los precios pueden haber cambiado.</Alert>
              </div>
            )}
          </Card>
        </div>
      </div>

      <Sheet
        open={converting}
        onClose={() => setConverting(false)}
        title="Convertir en solicitud"
        wide
        footer={
          <>
            <Button variant="ghost" onClick={() => setConverting(false)}>
              Cancelar
            </Button>
            <Button variant="primary" icon="check" busy={busy} disabled={!q.patient_id && !patient} onClick={() => void convert()}>
              Crear solicitud por {money(q.total_cents)}
            </Button>
          </>
        }
      >
        <p className="muted small">Los precios cotizados se respetan aunque el catálogo haya cambiado. El cobro se registra en la solicitud.</p>
        {!q.patient_id && (
          <Field label="Paciente">
            {patient ? (
              <SelectedPatient patient={patient} onChange={() => setPatient(null)} />
            ) : (
              <div className="stack">
                <PatientSearch onPick={setPatient} autoFocus placeholder={`Buscar a ${q.prospect_name ?? "la persona"}…`} />
                <Button variant="secondary" icon="userPlus" onClick={() => setShowNew(true)}>
                  Registrar paciente nuevo
                </Button>
              </div>
            )}
          </Field>
        )}
        <Field label="Médico solicitante">
          <DoctorPicker value={doctor} onChange={setDoctor} />
        </Field>
        <Field label="Entrega de resultados">
          <div className="pills">
            <Pill on={channels.includes("whatsapp")} icon="whatsapp" onClick={() => setChannels((cs) => (cs.includes("whatsapp") ? cs.filter((c) => c !== "whatsapp") : [...cs, "whatsapp"]))}>
              WhatsApp
            </Pill>
            <Pill on={channels.includes("print")} icon="print" onClick={() => setChannels((cs) => (cs.includes("print") ? cs.filter((c) => c !== "print") : [...cs, "print"]))}>
              Impreso
            </Pill>
          </div>
        </Field>
      </Sheet>
      <QuoteWhatsAppSheet open={sending} onClose={() => setSending(false)} quoteId={q.id} />
      <Sheet open={showNew} onClose={() => setShowNew(false)} title="Registrar paciente" wide>
        <PatientForm
          initial={q.prospect_name ? { first_name: q.prospect_name.split(" ")[0] ?? "", paternal_surname: q.prospect_name.split(" ").slice(1).join(" "), maternal_surname: "", birth_date: "", sex: "F", phone: "", email: "", origin: "", notes: "" } : undefined}
          onSaved={(p: Patient) => {
            setPatient(p);
            setShowNew(false);
          }}
          onCancel={() => setShowNew(false)}
        />
      </Sheet>
    </>
  );
}
